import { useCustom } from '@refinedev/core';
import { isNotConnected } from '../../pages/homework/model';
import type { AssignTarget } from '../../pages/homework/AssignHomeworkDrawer';

/**
 * The two reads the client-page header's Assign drawer makes (`ClientPageHeader.tsx`), lifted so a section's Assign says exactly what the
 * header's says (not connected / the app cannot show homework yet): the client's zone, language and `homeworkAppReady`, and the writes'
 * own question `clientHomeworkAccess{canAssign}`. Enabled only while the drawer is open.
 */
export const useAssignClient = (
  clientId: string,
  clientName: string,
  open: boolean,
): { target: AssignTarget; clientTimeZone: string | null } => {
  const { data: clientData } = useCustom<{
    timezone?: string | null;
    lng?: string | null;
    homeworkAppReady?: boolean | null;
  }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'client',
      fields: ['_id', 'timezone', 'lng', 'homeworkAppReady'],
      variables: { id: { name: 'id', type: 'ID', value: clientId } },
    },
    queryOptions: { enabled: open && Boolean(clientId) },
  });
  const { data: accessData } = useCustom<{ canAssign: boolean }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientHomeworkAccess',
      fields: ['canAssign'],
      variables: { subject: { name: 'subject', type: 'ID!', value: clientId } },
    },
    queryOptions: { enabled: open && Boolean(clientId), retry: false },
  });
  return {
    target: {
      subject: clientId,
      name: clientName,
      appReady: clientData?.data?.homeworkAppReady ?? undefined,
      notConnected: isNotConnected(false, accessData?.data?.canAssign),
      lng: clientData?.data?.lng,
    },
    clientTimeZone: clientData?.data?.timezone ?? null,
  };
};
